import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, Compass, Sparkles } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { BookButton, WhatsAppButton } from "../components/CTAButtons";
import { AREAS, getArea, SERVICES } from "../data/content";
import { breadcrumbSchema, multiGraph } from "../components/Schema";
import NotFound from "./NotFound";

export default function AreaDetail({ onBook }) {
  const { slug } = useParams();
  const area = getArea(slug);
  if (!area) return <NotFound />;

  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Areas", href: "/areas/" },
    { label: area.name },
  ];
  const services = area.services.map((s) => SERVICES.find((x) => x.slug === s)).filter(Boolean);
  const otherAreas = AREAS.filter((a) => a.slug !== area.slug).slice(0, 4);

  return (
    <>
      <Seo
        title={area.headline}
        description={area.intro.slice(0, 158)}
        path={`/areas/${area.slug}/`}
        image={area.image}
        jsonLd={multiGraph(breadcrumbSchema(crumbs))}
      />
      <section
        className="detail-hero area-hero"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(15,23,42,.82), rgba(15,23,42,.4)), url(${area.image})` }}
      >
        <div className="detail-hero-inner">
          <Breadcrumbs items={crumbs} />
          <p className="hero-label"><span className="pulse-dot" /> {area.kicker}</p>
          <h1>{area.headline}</h1>
          <p className="hero-copy">{area.intro}</p>
          <div className="hero-actions">
            <BookButton onBook={onBook} service={`Enquiry near ${area.name}`} label={`Enquire for a visit near ${area.name}`} testid="area-book-cta" />
            <WhatsAppButton
              className="btn btn-outline-light"
              label="WhatsApp our partner"
              message={`Hello, I'd like to enquire about a spa visit near ${area.name}, Bhubaneswar.`}
              source={`area_${area.slug}`}
              testid="area-whatsapp-cta"
            />
          </div>
        </div>
      </section>

      <section className="section-pad">
        <div className="area-grid-two">
          <article>
            {area.paragraphs.map((p, i) => <p key={i} className="large-copy">{p}</p>)}
            <h2>What's close by</h2>
            <div className="chip-row">
              {area.nearby.map((n) => <span key={n} className="chip">{n}</span>)}
            </div>
            <h2>Local tips</h2>
            <ul className="check-list">
              {area.tips.map((t) => <li key={t}><Check size={16} />{t}</li>)}
            </ul>
          </article>
          <aside className="info-box sticky">
            <Sparkles size={20} />
            <strong>Featured near {area.name}</strong>
            <span>Zorba International Spa — our featured spa partner for Bhubaneswar.</span>
            <small>Service details and availability are confirmed directly before you visit.</small>
            <BookButton onBook={onBook} service={`Enquiry near ${area.name}`} label="Start enquiry" testid="area-aside-book" />
            <WhatsAppButton
              label="WhatsApp"
              className="btn btn-secondary"
              message={`Hello, I'd like to enquire about a spa visit near ${area.name}, Bhubaneswar.`}
              source={`area_aside_${area.slug}`}
              testid="area-aside-whatsapp"
            />
          </aside>
        </div>
      </section>

      <section className="section-pad service-section">
        <h2>Popular services near {area.name}</h2>
        <div className="service-grid">
          {services.map((s, i) => (
            <Link to={`/services/${s.slug}/`} className="service-card" key={s.slug} data-testid={`area-service-${i}`}>
              <div className="service-photo" style={{ backgroundImage: `url(${s.image})` }} />
              <div className="service-card-copy">
                <span className="card-kicker">{s.kicker}</span>
                <h3>{s.name}</h3>
                <p>{s.short}</p>
                <ArrowRight size={18} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="section-pad areas-band">
        <h2>Explore other parts of Bhubaneswar</h2>
        <div className="area-grid">
          {otherAreas.map((a, i) => (
            <Link to={`/areas/${a.slug}/`} key={a.slug} className="area-link" data-testid={`other-area-${i}`}>
              <Compass size={17} />
              <span>{a.name}</span>
              <ArrowRight size={16} />
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
